import React, { useEffect, useState } from 'react'
import AdminDashboardLayout from './AdminDashboardLayout'
import { useParams, useNavigate } from 'react-router-dom'
import { getSingleProfileAPI, UpdateProfileReviewStatusAPI } from '../../redux/slices/profileReviewSlice'
import { useDispatch, useSelector } from 'react-redux'
import { base, errorHandler } from '../../constants/base'
import { toast } from 'react-toastify'
import './ProfileViewPage.css'

const ProfileViewPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const singleProfile = useSelector(state => state?.superAdminProfileReview?.singleProfile)
    const btnLoading = useSelector(state => state?.superAdminProfileReview?.btnLoading)
    const loading = useSelector(state => state?.superAdminProfileReview?.loading)
    const param = useParams()
    const id = param?.id

    const [showBlockModal, setShowBlockModal] = useState(false);
    const [reasonForBlock, setReasonForBlock] = useState("");

    const fetchSingleProfile = async () => {
        await dispatch(getSingleProfileAPI(id))
    }

    useEffect(() => {
        fetchSingleProfile()
    }, [id])

    const handleProfileReviewStatus = async (profileId, newStatus) => {
        const statusUpdate = {
            id: profileId,
            status: newStatus,
            reason: reasonForBlock
        }
        const res = await dispatch(UpdateProfileReviewStatusAPI(statusUpdate))
        if (res.type === 'UpdateProfileReviewStatusAPI/fulfilled') {
            if (res.payload.success || res.payload.status || res.payload.data.status === 200) {
                toast.success("Status updated successfully");
                fetchSingleProfile();
                setShowBlockModal(false);
                setReasonForBlock("");
            } else {
                errorHandler(res.payload.errors);
            }
        } else if (res.type === 'UpdateProfileReviewStatusAPI/rejected') {
            errorHandler(res.payload.errors);
        } else {
            toast.error('Sorry, something went wrong!');
        }
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'status-active';
            case 'block':
            case 'blocked':
                return 'status-blocked';
            default:
                return 'status-pending';
        }
    };

    const getRoleName = () => {
        const role = singleProfile?.role?.name?.toLowerCase();
        if (role === 'corporate') return 'Corporate';
        if (role === 'professional') return 'Professional';
        return 'User';
    };

    return (
        <AdminDashboardLayout>
            <div className="profile-view-page">
                {/* Page Header */}
                <div className="pv-header">
                    <button className="pv-back-btn" onClick={() => navigate('/admin/profile/review')}>
                        <i className="fas fa-arrow-left"></i>
                        <span>Back to Profile Review</span>
                    </button>
                    <h1 className="pv-title">Profile Details</h1>
                </div>

                {loading ? (
                    <div className="pv-loading">
                        <div className="pv-loader"></div>
                    </div>
                ) : (
                    <div className="pv-content">
                        {/* Profile Card */}
                        <div className="pv-profile-card">
                            <div className="pv-profile-header">
                                <div className="pv-avatar-section">
                                    {singleProfile?.images?.[0]?.path ? (
                                        <img
                                            src={`${base.imageUrl}${singleProfile?.images?.[0]?.path}`}
                                            alt={singleProfile?.name}
                                            className="pv-avatar"
                                        />
                                    ) : (
                                        <div className="pv-avatar-placeholder">
                                            <i className="fas fa-user"></i>
                                        </div>
                                    )}
                                </div>
                                <div className="pv-profile-info">
                                    <h2 className="pv-name">{singleProfile?.name || '-'}</h2>
                                    <p className="pv-email">{singleProfile?.email || '-'}</p>
                                    <div className="pv-badges">
                                        <span className="pv-role-badge">{getRoleName()}</span>
                                        {singleProfile?.status && (
                                            <span className={`pv-status-badge ${getStatusClass(singleProfile?.status)}`}>
                                                {singleProfile?.status}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="pv-actions">
                                    {singleProfile?.status?.toLowerCase() === 'active' ? (
                                        <button
                                            className="pv-action-btn block"
                                            onClick={() => setShowBlockModal(true)}
                                            disabled={btnLoading}
                                        >
                                            <i className="fas fa-ban"></i>
                                            <span>Block</span>
                                        </button>
                                    ) : (
                                        <button
                                            className="pv-action-btn approve"
                                            onClick={() => handleProfileReviewStatus(singleProfile?.id, 'active')}
                                            disabled={btnLoading}
                                        >
                                            {btnLoading ? (
                                                <span className="pv-btn-loader"></span>
                                            ) : (
                                                <>
                                                    <i className="fas fa-check"></i>
                                                    <span>Approve</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div className="pv-details-grid">
                            {/* Contact Information */}
                            <div className="pv-details-card">
                                <div className="pv-card-header">
                                    <i className="fas fa-address-book"></i>
                                    <h3>Contact Information</h3>
                                </div>
                                <div className="pv-card-body">
                                    {singleProfile?.phone_no && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-phone"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Phone Number</span>
                                                <span className="pv-info-value">{singleProfile?.phone_no}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleProfile?.address?.address && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-location-dot"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Address</span>
                                                <span className="pv-info-value">{singleProfile?.address?.address}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Identification */}
                            <div className="pv-details-card">
                                <div className="pv-card-header">
                                    <i className="fas fa-id-card"></i>
                                    <h3>Identification</h3>
                                </div>
                                <div className="pv-card-body">
                                    {singleProfile?.ein_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-briefcase"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">EIN Number</span>
                                                <span className="pv-info-value">{singleProfile?.ein_number}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleProfile?.tax_id_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-shield-halved"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Tax ID Number</span>
                                                <span className="pv-info-value">{singleProfile?.tax_id_number}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleProfile?.ss_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-address-card"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Social Security Number</span>
                                                <span className="pv-info-value">{singleProfile?.ss_number}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleProfile?.work_permit_id && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-hashtag"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Work Permit ID</span>
                                                <span className="pv-info-value">{singleProfile?.work_permit_id}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleProfile?.registration_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-registered"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Registration Number</span>
                                                <span className="pv-info-value">{singleProfile?.registration_number}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* About */}
                            {singleProfile?.about && (
                                <div className="pv-details-card pv-full-width">
                                    <div className="pv-card-header">
                                        <i className="fas fa-info-circle"></i>
                                        <h3>About</h3>
                                    </div>
                                    <div className="pv-card-body">
                                        <p className="pv-about-text">{singleProfile?.about}</p>
                                    </div>
                                </div>
                            )}

                            {/* Documents */}
                            {(singleProfile?.images?.[1]?.path || singleProfile?.images?.[2]?.path) && (
                                <div className="pv-details-card pv-full-width">
                                    <div className="pv-card-header">
                                        <i className="fas fa-file-alt"></i>
                                        <h3>Documents</h3>
                                    </div>
                                    <div className="pv-card-body">
                                        <div className="pv-documents-grid">
                                            {singleProfile?.images?.[1]?.path && (
                                                <a
                                                    href={`${base.imageUrl}${singleProfile?.images?.[1]?.path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pv-document-item"
                                                >
                                                    <div className="pv-doc-icon">
                                                        <i className="fas fa-file-pdf"></i>
                                                    </div>
                                                    <div className="pv-doc-info">
                                                        <span className="pv-doc-name">Document 1</span>
                                                        <span className="pv-doc-action">View Document</span>
                                                    </div>
                                                    <i className="fas fa-external-link-alt pv-doc-link"></i>
                                                </a>
                                            )}
                                            {singleProfile?.images?.[2]?.path && (
                                                <a
                                                    href={`${base.imageUrl}${singleProfile?.images?.[2]?.path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pv-document-item"
                                                >
                                                    <div className="pv-doc-icon">
                                                        <i className="fas fa-file-pdf"></i>
                                                    </div>
                                                    <div className="pv-doc-info">
                                                        <span className="pv-doc-name">Supporting Document</span>
                                                        <span className="pv-doc-action">View Document</span>
                                                    </div>
                                                    <i className="fas fa-external-link-alt pv-doc-link"></i>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Block Modal */}
            {showBlockModal && (
                <div className="pv-modal-overlay" onClick={() => setShowBlockModal(false)}>
                    <div className="pv-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pv-modal-header">
                            <div className="pv-modal-icon">
                                <i className="fas fa-ban"></i>
                            </div>
                            <h3 className="pv-modal-title">Block Profile</h3>
                            <p className="pv-modal-subtitle">Please provide a reason for blocking this profile</p>
                            <button className="pv-modal-close" onClick={() => setShowBlockModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="pv-modal-body">
                            <div className="pv-form-group">
                                <label className="pv-form-label">Reason for blocking</label>
                                <textarea
                                    value={reasonForBlock}
                                    onChange={(e) => setReasonForBlock(e.target.value)}
                                    className="pv-form-textarea"
                                    placeholder="Enter the reason for blocking this profile..."
                                    rows="4"
                                ></textarea>
                            </div>
                        </div>
                        <div className="pv-modal-footer">
                            <button className="pv-modal-btn cancel" onClick={() => setShowBlockModal(false)}>
                                Cancel
                            </button>
                            <button
                                className="pv-modal-btn confirm"
                                onClick={() => handleProfileReviewStatus(singleProfile?.id, 'block')}
                                disabled={btnLoading}
                            >
                                {btnLoading ? (
                                    <span className="pv-btn-loader"></span>
                                ) : (
                                    <>
                                        <i className="fas fa-ban"></i>
                                        Block Profile
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminDashboardLayout>
    )
}

export default ProfileViewPage
